import React, { useRef, useState } from "react";
import { FiUploadCloud } from "react-icons/fi";
import { AiOutlineFilePdf, AiOutlineClose } from "react-icons/ai";
import { FaFileImage } from "react-icons/fa";
import { FaFileExcel } from "react-icons/fa";

const FileUpload = ({
  label = "Drag & drop files or click to upload",
  accept = ".jpg,.jpeg,.png,.pdf",
  multiple = false,
  value = [],
  onChange,
  className = "",
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const MAX_FILE_SIZE = 3 * 1024 * 1024;

  const handleClick = () => inputRef.current?.click();

  const isValidFileType = (file) => {
    const acceptedTypes = accept
      .split(",")
      .map((type) => type.trim().toLowerCase());

    return acceptedTypes.some((type) => {
      if (type.startsWith(".")) {
        return file.name.toLowerCase().endsWith(type);
      } else {
        return file.type === type;
      }
    });
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);

    const validTypeFiles = fileArray.filter(isValidFileType);
    const invalidTypeFiles = fileArray.filter((file) => !isValidFileType(file));

    const tooLargeFiles = validTypeFiles.filter(
      (file) => file.size > MAX_FILE_SIZE
    );
    const validFiles = validTypeFiles.filter(
      (file) => file.size <= MAX_FILE_SIZE
    );

    if (invalidTypeFiles.length > 0 || tooLargeFiles.length > 0) {
      let messages = [];

      if (invalidTypeFiles.length > 0) {
        messages.push(
          `Invalid file type: ${invalidTypeFiles.map((f) => f.name).join(", ")}`
        );
      }
      if (tooLargeFiles.length > 0) {
        messages.push(
          `File too large (max 3MB): ${tooLargeFiles
            .map((f) => f.name)
            .join(", ")}`
        );
      }
      setError(messages.join(" | "));
      if (!multiple) {
        onChange?.(null);
      }
      return;
    }

    setError("");

    const newValue = multiple
      ? [...(Array.isArray(value) ? value : []), ...validFiles]
      : validFiles[0];

    onChange?.(newValue);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleRemoveFile = (index) => {
    if (!multiple) {
      onChange?.(null);
    } else {
      const updated = [...value];
      updated.splice(index, 1);
      onChange?.(updated);
    }
  };

  const renderFileIcon = (file) => {
    const filename =
      typeof file === "string"
        ? file.toLowerCase()
        : file.name?.toLowerCase() || "";
    const mimetype = typeof file === "string" ? "" : file.type;

    const isPdf = filename.endsWith(".pdf") || mimetype === "application/pdf";
    const isImage =
      /\.(jpe?g|png)$/i.test(filename) || mimetype.startsWith("image/");
    const isExcelOrCsv =
      /\.(xlsx|xls|csv)$/i.test(filename) ||
      mimetype ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      mimetype === "application/vnd.ms-excel" ||
      mimetype === "text/csv";

    if (isPdf) return <AiOutlineFilePdf size={16} className="text-danger" />;
    if (isImage) return <FaFileImage size={16} className="text-success" />;
    if (isExcelOrCsv) return <FaFileExcel size={16} className="text-success" />;
    return null;
  };

  const renderFileName = (file) => {
    if (!file) return "Unnamed File";

    if (typeof file === "string") {
      return file.split("/").pop();
    }

    if (file.name) {
      return file.name;
    }

    // fallback: maybe a Blob without name
    return "Unnamed File";
  };

  const filesToShow = Array.isArray(value) ? value : value ? [value] : [];

  return (
    <div
      className={`file-upload-zone ${
        isDragging ? "dragging" : ""
      } ${className}`}
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <input
        key={filesToShow.length} // ensures re-upload works
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => handleFiles(e.target.files)}
        hidden
      />
      <FiUploadCloud className="text-info mb-1" size={25} />
      {filesToShow.length == 0 && (
        <p className="upload-label mb-1" style={{ fontSize: "0.8rem" }}>
          {label}
        </p>
      )}

      {filesToShow.length > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-1">
          {filesToShow.map((file, idx) => (
            <div
              key={idx}
              className="d-flex align-items-center justify-content-between bg-light rounded border gap-1"
              style={{ padding: "0.2rem 0.4rem" }}
            >
              <div className="d-flex align-items-center gap-1">
                {renderFileIcon(file)}
                <span
                  className="text-dark text-break"
                  style={{ fontSize: "0.7rem" }}
                >
                  {renderFileName(file)}
                </span>
              </div>
              <span
                className="btn btn-sm btn-outline-danger rounded-1 d-flex align-items-center justify-content-center"
                style={{ padding: "0.1rem 0.1rem" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile(idx);
                }}
              >
                <AiOutlineClose size={10} className="text-darker" />
              </span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <span
          className="text-danger mt-1 text-break"
          style={{ fontSize: "0.8rem" }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default FileUpload;
